import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileSpreadsheet, RefreshCw, FileBarChart } from "lucide-react";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

const MONEY_FIELDS = new Set([
  "total_in", "refunds", "net", "revenue", "collected", "amount", "gst",
  "total", "gst_collected", "taxable_amount", "paid", "balance",
]);

// Column config mirrors backend
const COLUMNS = {
  daily_collection: [["date", "Date"], ["count", "Txn"], ["total_in", "Collected"], ["refunds", "Refunds"], ["net", "Net"]],
  monthly_revenue: [["month", "Month"], ["count", "Txn"], ["revenue", "Revenue"], ["refunds", "Refunds"], ["net", "Net"]],
  outstanding_bills: [["hospital_id", "Hospital ID"], ["name", "Patient"], ["bed", "Bed"], ["consultant", "Consultant"], ["days_admitted", "Days"], ["net", "Net"], ["paid", "Paid"], ["balance", "Balance"]],
  payment_method: [["method", "Method"], ["count", "Txn"], ["collected", "Collected"], ["refunds", "Refunds"], ["net", "Net"]],
  consultant_revenue: [["consultant", "Consultant"], ["count", "Txn"], ["revenue", "Revenue"]],
  department_revenue: [["department", "Department"], ["count", "Items"], ["amount", "Amount"], ["gst", "GST"], ["total", "Total"]],
  gst_report: [["rate", "GST %"], ["items", "Items"], ["taxable_amount", "Taxable"], ["gst_collected", "GST Collected"], ["total", "Total"]],
  discount_report: [["at", "Date"], ["patient", "Patient"], ["hospital_id", "Hospital ID"], ["kind", "Kind"], ["value", "Value"], ["reason", "Reason"], ["approved_by", "By"]],
  refund_report: [["at", "Date"], ["receipt_no", "Receipt"], ["patient", "Patient"], ["hospital_id", "Hospital ID"], ["method", "Method"], ["reference", "Reference"], ["amount", "Amount"], ["taken_by", "By"]],
};

const NEEDS_DATE_RANGE = ["daily_collection", "payment_method", "consultant_revenue", "department_revenue", "gst_report", "discount_report", "refund_report"];
const NEEDS_YEAR = ["monthly_revenue"];
const NEEDS_MIN_AMOUNT = ["outstanding_bills"];

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

export default function BillingReports() {
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState("daily_collection");
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [year, setYear] = useState(new Date().getFullYear());
  const [minAmount, setMinAmount] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/reports").then(({ data: d }) => setKinds(d)).catch(() => {});
  }, []);

  const paramString = useMemo(() => {
    const p = new URLSearchParams();
    if (NEEDS_DATE_RANGE.includes(kind)) { p.set("from_", from); p.set("to", to); }
    if (NEEDS_YEAR.includes(kind)) p.set("year", year);
    if (NEEDS_MIN_AMOUNT.includes(kind)) p.set("min_amount", minAmount || 0);
    return p.toString();
  }, [kind, from, to, year, minAmount]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/billing/reports/${kind}?${paramString}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  }, [kind, paramString]);

  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/billing/reports/${kind}.${fmt}?${paramString}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      if (fmt === "xlsx") {
        const a = document.createElement("a");
        a.href = url; a.download = `${kind}.xlsx`; a.click();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { toast.error("Export failed"); }
  };

  const cols = COLUMNS[kind] || [];

  const fmtCell = (row, key) => {
    const v = row[key];
    if (v == null || v === "") return "—";
    if (MONEY_FIELDS.has(key) && typeof v === "number") return inr(v);
    if (typeof v === "number") return v;
    return String(v);
  };

  return (
    <div className="space-y-6" data-testid="billing-reports">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Billing</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Financial reports with PDF &amp; Excel export.</p>
      </div>

      {/* Controls */}
      <div className="card-clinical p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <Label>Report</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="sel-report"><SelectValue /></SelectTrigger>
              <SelectContent>
                {kinds.map((k) => <SelectItem key={k.kind} value={k.kind}>{k.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {NEEDS_DATE_RANGE.includes(kind) && (
            <>
              <div>
                <Label>From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rep-from" />
              </div>
              <div>
                <Label>To</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="rep-to" />
              </div>
            </>
          )}
          {NEEDS_YEAR.includes(kind) && (
            <div>
              <Label>Year</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="rep-year" />
            </div>
          )}
          {NEEDS_MIN_AMOUNT.includes(kind) && (
            <div>
              <Label>Min. balance</Label>
              <Input type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} data-testid="rep-min-amount" />
            </div>
          )}
          <div className="flex flex-wrap gap-2 md:col-start-6 md:justify-end">
            <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="rep-run">
              <RefreshCw size={13} className="mr-1.5" /> Run
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => download("pdf")} data-testid="rep-pdf">
              <FileDown size={13} className="mr-1.5" /> PDF
            </Button>
            <Button size="sm" className="rounded-full" onClick={() => download("xlsx")} data-testid="rep-xlsx">
              <FileSpreadsheet size={13} className="mr-1.5" /> Excel
            </Button>
          </div>
        </div>
        {data && (
          <div className="text-xs text-muted-foreground border-t pt-2 flex flex-wrap gap-x-6 gap-y-1">
            <span>Period: <b className="text-foreground">{data.period}</b></span>
            {data.summary && Object.entries(data.summary).map(([k, v]) => (
              <span key={k}>
                {k}: <b className="text-foreground tabular">{MONEY_FIELDS.has(k) || (typeof v === "number" && !["count", "days", "consultants", "departments", "items"].includes(k)) ? inr(v) : String(v)}</b>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-clinical p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <FileBarChart size={14} className="text-primary" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Results ({(data?.rows || []).length})</span>
        </div>
        {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {!loading && (data?.rows || []).length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No rows for the selected period.</div>
        )}
        {!loading && (data?.rows || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  {cols.map(([k, label]) => (
                    <th key={k} className={`px-3 py-2 font-medium text-left ${MONEY_FIELDS.has(k) ? "text-right" : ""}`}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((row, i) => {
                  // Rows come from a JSON aggregation with no natural PK — build a stable key
                  // from the first two column values so re-renders don't reset input state.
                  const rowKey = cols.slice(0, 2).map(([k]) => String(row[k] ?? "")).join("|") || String(i);
                  return (
                    <tr key={rowKey} className="border-t border-border">
                      {cols.map(([k]) => (
                        <td key={k} className={`px-3 py-2 ${MONEY_FIELDS.has(k) ? "text-right tabular" : ""}`}>
                          {fmtCell(row, k)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
